import { type VariablesOf } from '@graphql-typed-document-node/core'
import { useAtomValue, useSetAtom } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'

import { brandGroupIdAtom, surveyFilterAtom } from '~/atoms'
import { graphql } from '~/gql'
import { useTabulate } from '~/hooks/useTabulate'
import { exists } from '~/utils'

import {
  fiveSegmentCommentAtom,
  isPendingTabulationAtom,
  isTabulationReadyAtom,
  lastTabulatedVariablesAtom,
  nineSegmentCommentAtom,
  savedFiveSegmentCommentAtom,
  savedNineSegmentCommentAtom,
  tabulationQueryDataAtom,
} from '../atoms'

import { useSurveyId } from './useSurveyId'

const mutationDocument = graphql(`
  mutation CustomerAnalysisPage_TabulationMutation(
    $surveyId: String!
    $brandGroupId: Int!
    $filter: [[CustomerAttributeFilterItemInput!]!]!
  ) {
    count: countBrands9segsInBrandGroup(
      surveyId: $surveyId
      brandGroupId: $brandGroupId
      customFilter: { filter: $filter }
    ) {
      awaitId
    }
  }
`)
type MutationVariables = VariablesOf<typeof mutationDocument>

const queryDocument = graphql(`
  query CustomerAnalysisPage_Tabulation($awaitId: String!) {
    count: countBrands9segsInBrandGroup(awaitId: $awaitId) {
      arrived
      result {
        ...CustomerAnalysisPage_TabulationQueryData
      }
    }
  }
`)

export const useTabulationQuery = () => {
  const isTabulationReady = useAtomValue(isTabulationReadyAtom)
  const isPendingTabulation = useAtomValue(isPendingTabulationAtom)

  const surveyFilter = useAtomValue(surveyFilterAtom)
  const brandGroupId = useAtomValue(brandGroupIdAtom)
  const surveyId = useSurveyId()

  const [tabulateState, tabulate] = useTabulate(mutationDocument, queryDocument)

  const setTabulationQueryData = useSetAtom(tabulationQueryDataAtom)
  const setLastTabulatedVariables = useSetAtom(lastTabulatedVariablesAtom)

  const resetFiveSegmentComment = useResetAtom(fiveSegmentCommentAtom)
  const resetSavedFiveSegmentComment = useResetAtom(savedFiveSegmentCommentAtom)
  const resetNineSegmentComment = useResetAtom(nineSegmentCommentAtom)
  const resetSavedNineSegmentComment = useResetAtom(savedNineSegmentCommentAtom)

  const hasTabulatedRef = React.useRef(false)

  const tabulateIfNeeded = React.useCallback(async () => {
    if (!isTabulationReady || isPendingTabulation || !exists(brandGroupId)) {
      return
    }

    const variables: MutationVariables = {
      surveyId,
      brandGroupId,
      filter: surveyFilter?.filterGroups ?? [],
    }
    const queryData = await tabulate(variables)

    setTabulationQueryData(queryData)
    setLastTabulatedVariables(variables)

    if (hasTabulatedRef.current) {
      resetFiveSegmentComment()
      resetSavedFiveSegmentComment()
      resetNineSegmentComment()
      resetSavedNineSegmentComment()
    }
    hasTabulatedRef.current = true
  }, [
    brandGroupId,
    isPendingTabulation,
    isTabulationReady,
    resetFiveSegmentComment,
    resetNineSegmentComment,
    resetSavedFiveSegmentComment,
    resetSavedNineSegmentComment,
    setLastTabulatedVariables,
    setTabulationQueryData,
    surveyFilter?.filterGroups,
    surveyId,
    tabulate,
  ])

  React.useEffect(() => {
    tabulateIfNeeded()
  }, [tabulateIfNeeded])

  return tabulateState.loading
}
